import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "./button";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

interface TreeSelectProps {
	name: string;
	defaultValue?: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	ref?: React.Ref<HTMLInputElement>;
	placeholder?: string;
	data: TreeNode[];
}
export function TreeSelect(props: TreeSelectProps) {
	const { defaultValue, onChange, data } = props;
	const [internalValue, setInternalValue] = useState(defaultValue ?? "");
	const popoverButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		//Hopefully this will prevent re-rendering the component if the defaultValue is the same as internalValue
		setInternalValue((internalValue) => {
			if (internalValue === defaultValue) return internalValue;
			return defaultValue ?? "";
		});
	}, [defaultValue]);

	const handleSelect = useCallback(
		(node: TreeNode) => {
			onChange?.({
				target: { name: props.name, value: node.id },
			} as React.ChangeEvent<HTMLInputElement>); // you can use a more specific type if needed
			setInternalValue(node.id);
		},
		[onChange, props.name],
	);

	return (
		<>
			<input
				value={internalValue}
				onChange={() => {}} // suppress React warning
				name={props.name}
				ref={props.ref}
				style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
				onFocus={() => popoverButtonRef.current?.focus()}
			/>
			<PopoverMemo
				data={data}
				placeholder={props.placeholder}
				value={internalValue}
				onSelect={handleSelect}
				ref={popoverButtonRef}
			/>
		</>
	);
}
export type TreeNode = {
	id: string;
	label: string;
	children?: TreeNode[];
};

const PopoverMemo = memo(PopoverSelect);
interface PopoverSelectProps {
	placeholder?: string;
	data: TreeNode[];
	value: string;
	onSelect: (node: TreeNode) => void;
	ref?: React.Ref<HTMLButtonElement>;
}
function PopoverSelect(props: PopoverSelectProps) {
	const { onSelect, data, value } = props;
	const sortedData = useMemo(() => {
		const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
			return [...nodes]
				.sort((a, b) => {
					const aIsFolder = !!a.children;
					const bIsFolder = !!b.children;

					
					if (aIsFolder && !bIsFolder) return -1;
					if (!aIsFolder && bIsFolder) return 1;

					return a.label.localeCompare(b.label, "es", {
						sensitivity: "base",
					});
				})
				.map((node) => ({
					...node,
					children: node.children ? sortNodes(node.children) : undefined,
				}));
		};

		return sortNodes(data);
	}, [data]);
	const [open, setOpen] = useState(false);
	const [breadcrumb, setBreadcrumb] = useState<TreeNode[]>([]);

	const parentMap = useMemo(() => {
		const parentMap: Record<string, TreeNode> = {};
		const traverse = (node: TreeNode, parent?: TreeNode) => {
			const newNode = { ...node, parent };
			if (parent) {
				parentMap[node.id] = parent;
			}
			if (node.children) {
				node.children.forEach((data) => traverse(data, newNode));
			}
		};
		sortedData.forEach((data) => traverse(data));

		return parentMap;
	}, [sortedData]);

	useEffect(() => {
		if (!open) return;
		if (!value || value == "-1") return;

		if (value) {
			let currentNode = parentMap[value];
			const path: TreeNode[] = [];
			while (currentNode) {
				path.unshift(currentNode);
				currentNode = parentMap[currentNode.id];
			}

			setBreadcrumb(path);
		}
	}, [value, open, data, parentMap]);

	const currentLevel = useMemo(() => {
		return breadcrumb.length === 0
			? sortedData
			: (breadcrumb[breadcrumb.length - 1]?.children ?? []);
	}, [breadcrumb, sortedData]);

	const handleNavigate = (node: TreeNode) => {
		setBreadcrumb([...breadcrumb, node]);
	};

	const handleSelect = (node: TreeNode) => {
		onSelect(node);
		setOpen(false);
	};

	const indexedDataLabels = useMemo(() => {
		const indexLabels = (nodes: TreeNode[]): Record<string, string> => {
			const labels: Record<string, string> = {};

			nodes.forEach((node) => {
				if (node.children) {
					const childLabels = indexLabels(node.children);
					Object.assign(labels, childLabels);
				} else {
					labels[node.id] = node.label;
				}
			});

			return labels;
		};

		return indexLabels(sortedData);
	}, [sortedData]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="input"
					className="pr-1"
					role="combobox"
					type="button"
					ref={props.ref}
					aria-expanded={open}
					aria-haspopup="listbox"
				>
					{indexedDataLabels[value] ?? props.placeholder ?? "Select something"}
					<ChevronDown />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-100 max-h-[400px] flex flex-col"
				align="start"
			>
				<Breadcrumb breadcrumb={breadcrumb} setBreadcrumb={setBreadcrumb} />
				{open && (
					<div className="overflow-y-auto overflow-x-hidden">
						<AnimatePresence mode="wait" initial={false}>
							{/* <ul className="space-y-1"> */}
							<motion.ul
								key={breadcrumb.map((n) => n.id).join("/") || "root"}
								initial={{ opacity: 0, x: 10 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -10 }}
								transition={{ duration: 0.15 }}
								className="space-y-1"
							>
								<BackItem
									breadcrumb={breadcrumb}
									setBreadcrumb={setBreadcrumb}
								/>
								{currentLevel.map((node) => (
									<SelectItem
										key={node.id}
										node={node}
										handleNavigate={handleNavigate}
										handleSelect={handleSelect}
									/>
								))}
							</motion.ul>
						</AnimatePresence>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

interface BreadcrumbProps {
	breadcrumb: TreeNode[];
	setBreadcrumb: (breadcrumb: TreeNode[]) => void;
}
function Breadcrumb(props: BreadcrumbProps) {
	const { breadcrumb, setBreadcrumb } = props;

	const handleBack = (index: number) => {
		setBreadcrumb(breadcrumb.slice(0, index + 1));
	};

	return (
		<div className="text-sm mb-2">
			{breadcrumb.length > 0 ? (
				<div className="flex gap-1 flex-wrap">
					<button onClick={() => setBreadcrumb([])} className="underline">
						Root
					</button>
					{breadcrumb.map((node, i) => (
						<span key={node.id}>
							/{" "}
							<button onClick={() => handleBack(i)} className="underline">
								{node.label}
							</button>
						</span>
					))}
				</div>
			) : (
				<div className="text-muted-foreground">Root</div>
			)}
		</div>
	);
}

interface SelectItemProps {
	node: TreeNode;
	handleNavigate: (to: TreeNode) => void;
	handleSelect: (value: TreeNode) => void;
}
function SelectItem(props: SelectItemProps) {
	const { node, handleNavigate, handleSelect } = props;

	return (
		<li>
			<button
				className="w-full text-left px-2 py-1 hover:bg-muted rounded"
				onClick={() => {
					if (node.children) {
						handleNavigate(node);
					} else {
						handleSelect(node);
					}
				}}
			>
				{node.children ? "📁" : "✅"} {node.label}
			</button>
		</li>
	);
}

interface BackItemProps {
	breadcrumb: TreeNode[];
	setBreadcrumb: (breadcrumb: TreeNode[]) => void;
}
function BackItem(props: BackItemProps) {
	const { setBreadcrumb, breadcrumb } = props;
	if (breadcrumb.length === 0) return null;
	const newBreadcrumb = breadcrumb.slice(0, -1);

	return (
		<li>
			<button
				className="w-full text-left px-2 py-1 hover:bg-muted rounded"
				onClick={() => setBreadcrumb(newBreadcrumb)}
			>
				🔙 Back
			</button>
		</li>
	);
}
